/**
 * AfgerondSignaal — "Toekomstige taak staat al op Afgerond — klopt dat?" (9 okt 2026).
 *
 * Op 9 okt werden vier FBTO-taken in Airtable aangemaakt met het vinkje Afgerond al aan (gekopieerd
 * van een afgeronde taak). Toolframe toont afgeronde taken niet, dus ze waren onzichtbaar en het leek
 * een sync-fout. De engine zoekt zulke taken (alleen lezen uit Airtable, 10 min cache) via
 * GET /Api/taak-signaal/afgerond; dit is de rustige regel bovenaan Rooster, Capaciteit en Dagstart.
 *
 * Rust (feedback Joris): alleen zichtbaar als er gevallen zijn, één regel, het lijstje achter een klik.
 * "Nu ophalen" draait de taken-delta (Airtable lezen → onze database), schrijft niets naar Airtable.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography,
} from '@mui/material';
import { BACKEND_URL } from '../utils/apiConfig';
import { authFetch } from '../utils/authHelpers';

const PAD = '/Api/taak-signaal/afgerond';
const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export const korteDatum = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${Number(m[3])} ${MAANDEN[Number(m[2]) - 1]}` : '';
};

export const periode = (t) => {
  const start = korteDatum(t.start);
  const eind = korteDatum(t.eind);
  if (!start) return eind ? `t/m ${eind}` : 'geen datum';
  return eind && eind !== start ? `${start} – ${eind}` : start;
};

export const signaalTekst = (n) => (n === 1
  ? '1 taak in de toekomst staat al op Afgerond en is daardoor onzichtbaar'
  : `${n} taken in de toekomst staan al op Afgerond en zijn daardoor onzichtbaar`);

export default function AfgerondSignaal({ sx }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState('');
  const [opgehaald, setOpgehaald] = useState(false);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}${PAD}`);
      if (r?.ok) setData(await r.json());
    } catch {
      // Een signaal mag een planningspagina nooit breken: geen regel is dan de uitkomst.
    }
  }, []);

  useEffect(() => { laad(); }, [laad]);

  const nuOphalen = async () => {
    setBezig(true);
    setMelding('');
    setOpgehaald(false);
    try {
      const r = await authFetch(`${BACKEND_URL}${PAD}/ophalen`, { method: 'POST' });
      const uit = r?.ok ? await r.json() : null;
      if (uit?.signaal) setData(uit.signaal);
      // De planning eronder heeft zijn eigen data: eerlijk zeggen dat die pas na verversen bijwerkt.
      setOpgehaald(Boolean(uit?.opgehaald));
      setMelding(uit?.melding || (uit?.opgehaald ? 'Opgehaald. Ververs de pagina om de taak in de planning te zien.' : 'Ophalen lukte niet.'));
    } catch {
      setMelding('Ophalen lukte niet.');
    } finally {
      setBezig(false);
    }
  };

  const taken = data?.beschikbaar ? data.taken || [] : [];
  const kanOphalen = data?.kan_ophalen !== false;
  // Een open dialoog blijft staan als het laatste geval na "Nu ophalen" verdwijnt (anders is de melding weg).
  if (!taken.length && !open) return null;

  return (
    <>
      {taken.length > 0 && (
        <Box
          data-testid="afgerond-signaal"
          sx={{ display: 'flex', alignItems: 'baseline', gap: 1, fontSize: '0.8rem', color: '#92400e', py: 0.5, ...sx }}
        >
          <span aria-hidden>⚠</span>
          <span>{signaalTekst(taken.length)}</span>
          <Link component="button" type="button" onClick={() => setOpen(true)}
            sx={{ fontSize: 'inherit', color: 'inherit', textDecorationColor: 'currentColor', verticalAlign: 'baseline' }}>
            bekijk
          </Link>
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Staan al op Afgerond</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {kanOphalen
              ? 'Zet het vinkje Afgerond uit in Airtable; daarna staat de taak binnen ±10 min in de tool.'
              : 'Zet het vinkje Afgerond uit in Airtable. In deze testomgeving worden taken niet automatisch uit Airtable opgehaald.'}
          </Typography>
          {taken.length === 0 && (
            <Typography variant="body2" sx={{ mb: 1 }}>Er staan geen taken meer in deze lijst.</Typography>
          )}
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {taken.map((t) => (
              <Box component="li" key={t.id} data-testid="afgerond-signaal-taak"
                sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25, borderTop: '1px solid #eef0f2' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={t.naam}>{t.naam}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {[(t.uitvoerders || []).join(', ') || 'niemand', periode(t), t.project || t.projectnummer]
                      .filter(Boolean).join(' · ')}
                  </Typography>
                  {t.aangemaakt_door && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                      Aangemaakt door {t.aangemaakt_door}{t.aangemaakt ? ` op ${korteDatum(t.aangemaakt)}` : ''}
                    </Typography>
                  )}
                </Box>
                <Button size="small" variant="outlined" href={t.airtable_url} target="_blank" rel="noopener noreferrer"
                  sx={{ flexShrink: 0, textTransform: 'none' }}>
                  Open in Airtable
                </Button>
              </Box>
            ))}
          </Box>
          {melding && (
            <Typography variant="caption" role="status" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
              {melding}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {opgehaald ? (
            <Button onClick={() => window.location.reload()} sx={{ textTransform: 'none' }}>Pagina verversen</Button>
          ) : kanOphalen && (
            <Button onClick={nuOphalen} disabled={bezig} sx={{ textTransform: 'none' }}>
              {bezig ? 'Bezig met ophalen…' : 'Nu ophalen'}
            </Button>
          )}
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
